// src/components/ecommerce/OrderDetailModal.jsx
import React, { useState } from 'react';
import { 
  X,
  ShoppingCart,
  User,
  MapPin,
  Phone,
  Mail,
  Package,
  Truck,
  CreditCard,
  Calendar,
  Clock,
  Edit,
  Save,
  MessageCircle,
  Printer,
  Download,
  Eye,
  CheckCircle,
  AlertTriangle,
  FileText,
  DollarSign
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';

export const OrderDetailModal = ({ order, storeData, isOpen, onClose, onStatusUpdate, onOrderUpdate }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [editingStatus, setEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState(order?.status || 'pending');
  const [trackingNumber, setTrackingNumber] = useState(order?.trackingNumber || '');
  const [notes, setNotes] = useState(order?.notes || '');

  const handleStatusUpdate = async () => {
    try {
      await onStatusUpdate(order.id, newStatus);
      setEditingStatus(false);
      onOrderUpdate?.();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const handlePrintOrder = () => {
    window.print();
  };

  const handleExportOrder = () => {
    const orderData = {
      orderNumber: order.orderNumber,
      customer: order.customerName,
      email: order.customerEmail,
      phone: order.customerPhone,
      address: order.shippingAddress,
      items: order.items,
      subtotal: order.subtotal,
      tax: order.tax,
      shipping: order.shipping,
      total: order.total,
      status: order.status,
      createdAt: order.createdAt
    };

    const dataStr = JSON.stringify(orderData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `order-${order.orderNumber}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-700 bg-yellow-100 border-yellow-200',
      confirmed: 'text-blue-700 bg-blue-100 border-blue-200',
      processing: 'text-purple-700 bg-purple-100 border-purple-200',
      shipped: 'text-indigo-700 bg-indigo-100 border-indigo-200',
      delivered: 'text-green-700 bg-green-100 border-green-200',
      cancelled: 'text-red-700 bg-red-100 border-red-200',
      refunded: 'text-gray-700 bg-gray-100 border-gray-200'
    };
    return colors[status] || colors.pending;
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: Clock,
      confirmed: CheckCircle,
      processing: Package,
      shipped: Truck,
      delivered: CheckCircle,
      cancelled: X,
      refunded: DollarSign
    };
    return icons[status] || Clock;
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'refunded', label: 'Refunded' }
  ];

  const tabs = [
    { id: 'details', label: 'Order Details', icon: ShoppingCart },
    { id: 'customer', label: 'Customer Info', icon: User },
    { id: 'shipping', label: 'Shipping', icon: Truck },
    { id: 'payment', label: 'Payment', icon: CreditCard },
    { id: 'notes', label: 'Notes', icon: FileText }
  ];

  if (!isOpen || !order) return null;

  const StatusIcon = getStatusIcon(order.status);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Order #{order.orderNumber}</h2>
              <p className="text-gray-600">
                Placed on {new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString()}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <StatusIcon className="h-5 w-5 text-gray-600" />
              {editingStatus ? (
                <div className="flex items-center gap-2">
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleStatusUpdate}
                    className="p-1 text-green-600 hover:bg-green-100 rounded"
                  >
                    <Save className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingStatus(false)}
                    className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(order.status)}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                  <button
                    onClick={() => setEditingStatus(true)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handlePrintOrder}
              className="p-2 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              title="Print Order"
            >
              <Printer className="h-4 w-4" />
            </button>
            
            <button
              onClick={handleExportOrder}
              className="p-2 text-gray-400 hover:text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
              title="Export Order"
            >
              <Download className="h-4 w-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 border-b-2 whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Order Summary */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Order Total</p>
                    <p className="text-xl font-bold text-gray-900">R{(order.total || 0).toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Items</p>
                    <p className="text-xl font-bold text-gray-900">{order.items?.length || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Payment Status</p>
                    <p className={`text-sm font-medium ${
                      order.paymentStatus === 'paid' ? 'text-green-600' :
                      order.paymentStatus === 'pending' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {order.paymentStatus || 'pending'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Order Date</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(order.createdAt?.toDate?.() || order.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {(order.items || []).map((item, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="h-10 w-10 bg-gray-200 rounded flex items-center justify-center">
                                {item.imageUrl ? (
                                  <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    className="h-10 w-10 object-cover rounded"
                                  />
                                ) : (
                                  <Package className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                {item.sku && (
                                  <div className="text-sm text-gray-500">SKU: {item.sku}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">{item.quantity}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">R{(item.price || 0).toFixed(2)}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            R{((item.price || 0) * (item.quantity || 0)).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Order Totals */}
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="text-gray-900">R{(order.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {order.tax > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax:</span>
                        <span className="text-gray-900">R{(order.tax || 0).toFixed(2)}</span>
                      </div>
                    )}
                    {order.shipping > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Shipping:</span>
                        <span className="text-gray-900">R{(order.shipping || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="border-t border-gray-300 pt-2 flex justify-between font-bold">
                      <span className="text-gray-900">Total:</span>
                      <span className="text-gray-900">R{(order.total || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'customer' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-900">{order.customerName || 'Guest Customer'}</span>
                    </div>
                    {order.customerEmail && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900">{order.customerEmail}</span>
                      </div>
                    )}
                    {order.customerPhone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900">{order.customerPhone}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    {order.customerEmail && (
                      <button
                        onClick={() => window.open(`mailto:${order.customerEmail}`)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        <Mail className="h-4 w-4" />
                        Email
                      </button>
                    )}
                    {order.customerPhone && (
                      <>
                        <button
                          onClick={() => window.open(`tel:${order.customerPhone}`)}
                          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                        >
                          <Phone className="h-4 w-4" />
                          Call
                        </button>
                        {storeData?.integrations?.whatsapp && (
                          <button
                            onClick={() => {
                              const message = `Hi! This is regarding your order ${order.orderNumber}. How can we help you?`;
                              window.open(`https://wa.me/${order.customerPhone}?text=${encodeURIComponent(message)}`);
                            }}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <MessageCircle className="h-4 w-4" />
                            WhatsApp
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'shipping' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Information</h3>
                
                {order.shippingAddress && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-900 mb-2">Delivery Address</h4>
                    <div className="flex items-start gap-3">
                      <MapPin className="h-4 w-4 text-gray-400 mt-1" />
                      <div className="text-gray-900 whitespace-pre-line">
                        {order.shippingAddress}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tracking Number
                    </label>
                    <input
                      type="text"
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter tracking number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Shipping Method
                    </label>
                    <p className="text-gray-900 py-2">{order.shippingMethod || 'Standard Delivery'}</p>
                  </div>
                </div>

                {trackingNumber && (
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        // Save tracking number
                        ecommerceService.updateOrder(order.id, { trackingNumber });
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Save Tracking Number
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Payment Status</h4>
                    <span className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${
                      order.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                      order.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {order.paymentStatus || 'pending'}
                    </span>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Payment Method</h4>
                    <p className="text-gray-600">{order.paymentMethod || 'Not specified'}</p>
                  </div>
                  
                  {order.transactionId && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Transaction ID</h4>
                      <p className="text-gray-600 font-mono text-sm">{order.transactionId}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Order Notes</h3>
              
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows="8"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Add notes about this order..."
              />
              
              <button
                onClick={() => {
                  // Save notes
                  ecommerceService.updateOrder(order.id, { notes });
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Save Notes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};